import React, { useMemo, useState } from "react";
import { BatchSession } from "../types/batchSession.types";

const ALL_ADMINS = "__all__";

interface Props {
  /** raw rows from /batches */
  batches: any[];
  /** all sessions (full history) */
  sessions: BatchSession[];
  admins: string[];
  /** admin currently being viewed on the page */
  defaultAdmin: string;
  /** today's date, YYYY-MM-DD (IST) */
  today: string;
}

const csvCell = (v: string) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/* Trainer status + recording status of the latest session a batch held on
 * `date`. Batches with no session that day report "no session". */
function buildCsv(
  batches: any[],
  sessions: BatchSession[],
  admin: string,
  date: string,
): { csv: string; total: number; done: number } {
  const byBatch = new Map<string, BatchSession>();
  sessions.forEach((s) => {
    if ((s.createdDate || "").split("T")[0] !== date) return;
    const prev = byBatch.get(s.batchNo);
    const t = s.lastUpdated || s.createdDate || "";
    if (!prev || t > (prev.lastUpdated || prev.createdDate || "")) {
      byBatch.set(s.batchNo, s);
    }
  });

  const rows = batches
    .filter((b) => (b.batchStatus || "").toLowerCase() === "on-going")
    .filter(
      (b) =>
        admin === ALL_ADMINS ||
        (b.admin || "").toLowerCase().trim() === admin.toLowerCase().trim(),
    )
    .sort(
      (a, b) =>
        (a.admin || "").localeCompare(b.admin || "") ||
        String(a.batch_no).localeCompare(String(b.batch_no), undefined, {
          numeric: true,
        }),
    )
    .map((b) => {
      const s = byBatch.get(b.batch_no);
      const trainer = (s?.trainer?.status || (s ? "pending" : "no session")).toLowerCase();
      const recording = (s?.recordingStatus || (s ? "none" : "no session")).toLowerCase();
      // finished: attended + available; live now: joined + recording
      const ok =
        (trainer === "attended" && recording === "available") ||
        (trainer === "joined" && recording === "recording");
      return [
        date,
        b.admin || "",
        b.batch_no || "",
        cap(trainer),
        cap(recording),
        ok ? "Yes" : "No",
      ];
    });

  const header = [
    "Date",
    "Admin",
    "Batch Number",
    "Trainer Status",
    "Recording Status",
    "Automation OK",
  ];
  const total = rows.length;
  const done = rows.filter((r) => r[5] === "Yes").length;
  const summary = [
    [],
    ["Total On-going Batches", String(total)],
    ["Automation Completed", String(done)],
    ["Automation Not Completed", String(total - done)],
  ];
  const csv = [header, ...rows, ...summary]
    .map((r) => r.map(csvCell).join(","))
    .join("\r\n");
  return { csv, total, done };
}

export default function DownloadReport({
  batches,
  sessions,
  admins,
  defaultAdmin,
  today,
}: Props) {
  const [open, setOpen] = useState(false);
  const [admin, setAdmin] = useState(defaultAdmin || ALL_ADMINS);
  const [date, setDate] = useState(today);

  const preview = useMemo(
    () => buildCsv(batches, sessions, admin, date),
    [batches, sessions, admin, date],
  );

  const download = () => {
    const { csv, total } = preview;
    if (total === 0) {
      alert("No on-going batches found for this selection.");
      return;
    }
    // BOM so Excel opens it as UTF-8
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `batch-report_${
      admin === ALL_ADMINS ? "all-admins" : admin.replace(/\s+/g, "-")
    }_${date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-[#221E45] hover:bg-[#2f2a5e] rounded-lg transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4"
          />
        </svg>
        Download Report
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 z-30 bg-white border border-slate-200 rounded-xl shadow-lg p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Admin
            </label>
            <select
              value={admin}
              onChange={(e) => setAdmin(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
            >
              <option value={ALL_ADMINS}>All Admins</option>
              {admins.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Date
            </label>
            <input
              type="date"
              value={date}
              max={today}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg"
            />
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-slate-50 border border-slate-200 py-2">
              <p className="text-base font-bold text-slate-800">{preview.total}</p>
              <p className="text-[11px] text-slate-500">On-going</p>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 py-2">
              <p className="text-base font-bold text-emerald-700">{preview.done}</p>
              <p className="text-[11px] text-emerald-700">Automated</p>
            </div>
            <div className="rounded-lg bg-red-50 border border-red-200 py-2">
              <p className="text-base font-bold text-red-600">
                {preview.total - preview.done}
              </p>
              <p className="text-[11px] text-red-600">Not done</p>
            </div>
          </div>
          <p className="text-xs text-slate-400">
            On-going batches only. Columns: Batch Number, Trainer Status,
            Recording Status. "Automated" = trainer attended + recording available, or trainer joined + recording (live).
          </p>
          <button
            onClick={download}
            className="w-full py-2 text-sm font-semibold text-white bg-[#221E45] hover:bg-[#2f2a5e] rounded-lg"
          >
            Download CSV
          </button>
        </div>
      )}
    </div>
  );
}
